import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowRight, Package, MapPin, Plus, History, Truck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Transfers = () => {
  const [transfers, setTransfers] = useState([]);
  const [parts, setParts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { canCreateTransfers } = useAuth();

  const [formData, setFormData] = useState({
    part_id: '',
    from_location_id: '',
    to_location_id: '',
    quantity: 1,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [transfersRes, partsRes, locationsRes] = await Promise.all([
        axios.get(`${API}/transfers`),
        axios.get(`${API}/parts`),
        axios.get(`${API}/locations`)
      ]);
      setTransfers(transfersRes.data);
      setParts(partsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      part_id: '',
      from_location_id: '',
      to_location_id: '',
      quantity: 1,
      notes: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.from_location_id === formData.to_location_id) {
      toast.error('Source and destination must be different');
      return;
    }
    
    try {
      await axios.post(`${API}/transfers`, formData);
      toast.success('Transfer completed successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transfer');
    }
  };

  const handlePartSelect = (partId) => {
    const part = parts.find(p => p.id === partId);
    setFormData({
      ...formData,
      part_id: partId,
      from_location_id: part?.location_id || ''
    });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const selectedPart = parts.find(p => p.id === formData.part_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="transfers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Transfers</h1>
          <p className="text-slate-500 mt-1">Transfer parts between locations</p>
        </div>
        {canCreateTransfers() && (
          <Button
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="new-transfer-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Transfer
          </Button>
        )}
      </div>

      {/* Transfer History */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardHeader className="border-b bg-slate-50">
          <CardTitle className="text-lg font-bold flex items-center gap-2">
            <History className="w-5 h-5" />
            Transfer History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {transfers.length > 0 ? (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Part</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead></TableHead>
                  <TableHead>To</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Transferred By</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transfers.map(transfer => (
                  <TableRow key={transfer.id} data-testid={`transfer-row-${transfer.id}`}>
                    <TableCell className="text-sm text-slate-500">
                      {formatDate(transfer.transferred_at)}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-mono font-semibold">{transfer.part_reference}</p>
                        <p className="text-xs text-slate-500 truncate max-w-[150px]">{transfer.part_description}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="rounded-sm">
                        <MapPin className="w-3 h-3 mr-1" />
                        {transfer.from_location_name}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-blue-100 text-blue-700 rounded-sm">
                        <MapPin className="w-3 h-3 mr-1" />
                        {transfer.to_location_name}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      {transfer.quantity}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {transfer.transferred_by}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500 max-w-[150px] truncate">
                      {transfer.notes || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Truck className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No transfers yet</p>
              <p className="text-sm">Transfer parts between locations to see history here</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Transfer Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold">New Transfer</DialogTitle>
            <DialogDescription>
              Transfer parts from one location to another
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Select Part *</Label>
              <Select
                value={formData.part_id}
                onValueChange={handlePartSelect}
                required
              >
                <SelectTrigger className="rounded-sm" data-testid="transfer-part-select">
                  <SelectValue placeholder="Select a part to transfer" />
                </SelectTrigger>
                <SelectContent>
                  {parts.map(part => (
                    <SelectItem key={part.id} value={part.id}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono">{part.reference_number}</span>
                        <span className="text-slate-500">-</span>
                        <span className="truncate max-w-[200px]">{part.description}</span>
                        <Badge variant="secondary" className="ml-2">Qty: {part.quantity}</Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedPart && (
              <div className="p-3 bg-slate-50 rounded-sm border">
                <div className="flex items-center gap-3">
                  <Package className="w-8 h-8 text-slate-400" />
                  <div>
                    <p className="font-mono font-semibold">{selectedPart.reference_number}</p>
                    <p className="text-sm text-slate-500">{selectedPart.description}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Current: <span className="font-semibold">{selectedPart.location_name}</span> • 
                      Available: <span className="font-semibold">{selectedPart.quantity}</span> units
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From Location *</Label>
                <Select
                  value={formData.from_location_id}
                  onValueChange={(value) => setFormData({ ...formData, from_location_id: value })}
                  required
                >
                  <SelectTrigger className="rounded-sm" data-testid="transfer-from-select">
                    <SelectValue placeholder="Source" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>To Location *</Label>
                <Select
                  value={formData.to_location_id}
                  onValueChange={(value) => setFormData({ ...formData, to_location_id: value })}
                  required
                >
                  <SelectTrigger className="rounded-sm" data-testid="transfer-to-select">
                    <SelectValue placeholder="Destination" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.filter(l => l.id !== formData.from_location_id).map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Quantity *</Label>
              <Input
                type="number"
                min="1"
                max={selectedPart?.quantity || 1}
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                className="rounded-sm"
                required
                data-testid="transfer-quantity-input"
              />
              {selectedPart && (
                <p className="text-xs text-slate-500">Max: {selectedPart.quantity} units available</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="rounded-sm"
                rows={2}
                placeholder="Optional notes about this transfer"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                data-testid="transfer-submit-btn"
              >
                <Truck className="w-4 h-4 mr-2" />
                Transfer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Transfers;
